import { useState } from "react";
import {
  Eye,
  EyeOff,
  FileText,
  LoaderCircle,
  Sparkles,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { cn } from "@/lib/utils";
import type { CredentialVaultScope } from "../../lib/credential-vault-scope";
import { ProviderEvidencePreview } from "./provider-evidence-preview";
import { credentialDocumentFormat } from "../../lib/credential-document-format";
import {
  BLOCKED_UPLOAD_LABEL,
  BLOCKED_UPLOAD_DESCRIPTION,
  isReviewUploadBlocked,
} from "../../lib/review-upload-security";
import type {
  Credential,
  CredentialDocument,
} from "@/features/credentials/types";
import type { PreviewDocument } from "@/features/credentials/hooks/use-credential-document-url";
import type { useCredentialVaultReview } from "./use-credential-vault-review";
import {
  candidateHasDifferentIdentity,
  type ProviderReviewItem,
} from "./provider-credential-review-values";

function reviewStatus({
  item,
  credential,
}: {
  item: ProviderReviewItem;
  credential: Credential;
}) {
  if (isReviewUploadBlocked(item)) {
    return BLOCKED_UPLOAD_DESCRIPTION;
  }
  if (item.state === "reading") {
    return "Parsing credential details…";
  }
  if (item.scanStatus !== "clean") {
    return "This file has not passed its file check and cannot be attached yet.";
  }
  if (
    item.candidate &&
    item.candidate.credentialType !== credential.credentialType
  ) {
    return "This file was identified as a different credential type. Enter the details manually to keep it as supporting evidence here.";
  }
  if (
    item.candidate &&
    candidateHasDifferentIdentity({ credential, candidate: item.candidate })
  ) {
    return "This file identifies a different credential. Its details were not applied; add a separate credential for them, or keep this file only as supporting evidence.";
  }
  if (item.candidate) {
    return "Details were read from this file. Review the proposed values before saving.";
  }
  return "We could not extract credential details. Enter them manually; this file can still be kept as evidence.";
}

export function ProviderCredentialDocuments({
  credential,
  scope,
  review,
  disabled,
  onOpenDocument,
  onOpenReviewDocument,
}: {
  credential: Credential;
  scope: CredentialVaultScope;
  review: ReturnType<typeof useCredentialVaultReview>;
  disabled: boolean;
  onOpenDocument?: (document: CredentialDocument) => void;
  onOpenReviewDocument?: (document: PreviewDocument) => void;
}) {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const previewDocuments = [
    ...credential.documents.map((document) => ({
      document,
      isReviewDocument: false,
      onExpand: onOpenDocument ? () => onOpenDocument(document) : undefined,
    })),
    ...review.items
      .filter((item) => item.scanStatus === "clean")
      .map((item) => {
        const document = {
          id: item.documentId,
          title: item.title,
          fileName: item.fileName,
        };
        return {
          document,
          isReviewDocument: true,
          onExpand: onOpenReviewDocument
            ? () => onOpenReviewDocument(document)
            : undefined,
        };
      }),
  ];
  const selectedPreview = previewDocuments.find(
    (item) => item.document.id === selectedDocumentId,
  );

  function togglePreview(documentId: string) {
    setSelectedDocumentId((current) =>
      current === documentId ? null : documentId,
    );
  }
  const hasKeptEvidence =
    credential.documents.some(
      (document) => !review.removedDocumentIds.has(document.id),
    ) || review.reviewDocuments.some((document) => document.attach);
  const hasDocuments =
    credential.documents.length > 0 || review.selectedIds.size > 0;
  return (
    <section className={PROFILE_FORM_STYLES.section}>
      <h3 className={PROFILE_FORM_STYLES.sectionHeading}>
        Supporting documents
      </h3>
      {review.selectedIds.size > 0 && review.query.isLoading && (
        <p role="status" className="text-sm text-slate-500">
          Loading uploaded files…
        </p>
      )}
      {review.selectedIds.size > 0 && review.query.isError && (
        <div role="alert" className={PROFILE_FORM_STYLES.error}>
          <p>Could not load all selected files. Your edits are still here.</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={review.query.isFetching}
            onClick={() => void review.query.refetch()}
          >
            Retry loading files
          </Button>
        </div>
      )}
      {hasDocuments && (
        <p className="text-xs leading-relaxed text-slate-500">
          Click a file to show or hide its preview. Checked files will stay
          attached when you save.
        </p>
      )}
      <div className="space-y-2">
        {credential.documents.map((document) => {
          const kept = !review.removedDocumentIds.has(document.id);
          const isPreviewOpen = selectedPreview?.document.id === document.id;
          const PreviewIcon = isPreviewOpen ? EyeOff : Eye;
          return (
            <div
              key={document.id}
              className={cn(
                "flex items-start gap-2.5 rounded-xl border p-3",
                isPreviewOpen
                  ? "border-primary/20 bg-primary/5"
                  : "border-slate-200 bg-white",
              )}
            >
              <input
                type="checkbox"
                aria-label={`Keep ${document.title || document.fileName}`}
                checked={kept}
                disabled={disabled}
                className="mt-1 size-4 accent-primary"
                onChange={(event) =>
                  review.setExistingDocumentKept(
                    document.id,
                    event.target.checked,
                  )
                }
              />
              <FileText
                className="mt-0.5 size-4 shrink-0 text-primary"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  className="text-left text-sm font-semibold leading-snug text-slate-700 hover:text-primary"
                  onClick={() => togglePreview(document.id)}
                  aria-expanded={isPreviewOpen}
                >
                  {document.title || document.fileName}
                </button>
                <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  {credentialDocumentFormat(document.fileName) || "Document"} ·
                  On file
                </p>
                {!kept && (
                  <p className="mt-1 text-xs text-amber-700">
                    Will be removed from this credential when you save.
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 rounded-lg px-2 text-primary"
                aria-label={`${isPreviewOpen ? "Hide preview of" : "Preview"} ${document.title || document.fileName}`}
                aria-expanded={isPreviewOpen}
                onClick={() => togglePreview(document.id)}
              >
                <PreviewIcon className="size-4" aria-hidden="true" />
              </Button>
            </div>
          );
        })}
        {review.items.map((item) => {
          const isBlocked = isReviewUploadBlocked(item);
          const ReviewIcon = isBlocked ? ShieldAlert : Sparkles;
          const kept = !review.omittedIds.has(item.documentId);
          const isParsing = !isBlocked && kept && item.state === "reading";
          const isPreviewOpen =
            selectedPreview?.document.id === item.documentId;
          const PreviewIcon = isPreviewOpen ? EyeOff : Eye;
          return (
            <div
              key={item.documentId}
              className={cn(
                "flex items-start gap-2.5 rounded-xl border p-3",
                isPreviewOpen || isParsing
                  ? "border-primary/20 bg-primary/5"
                  : "border-slate-200 bg-white",
              )}
            >
              {!isBlocked && (
                <input
                  type="checkbox"
                  aria-label={`Keep uploaded ${item.title || item.fileName}`}
                  checked={kept}
                  disabled={disabled}
                  className="mt-1 size-4 accent-primary"
                  onChange={(event) =>
                    review.setReviewDocumentKept(
                      item.documentId,
                      event.target.checked,
                    )
                  }
                />
              )}
              {isParsing ? (
                <LoaderCircle
                  className="mt-0.5 size-4 shrink-0 animate-spin text-primary"
                  aria-hidden="true"
                />
              ) : (
                <ReviewIcon
                  className={cn(
                    "mt-0.5 size-4 shrink-0 text-primary",
                    isBlocked && "text-amber-700",
                  )}
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0 flex-1 space-y-1">
                <button
                  type="button"
                  className="text-left text-sm font-semibold leading-snug text-slate-700 hover:text-primary disabled:cursor-default disabled:text-slate-700"
                  disabled={item.scanStatus !== "clean"}
                  onClick={() => togglePreview(item.documentId)}
                  aria-expanded={isPreviewOpen}
                >
                  {item.title || item.fileName}
                </button>
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  {credentialDocumentFormat(item.fileName) || "Document"} · New
                  upload
                </p>
                {isBlocked && (
                  <p className="text-xs font-semibold text-amber-800">
                    {BLOCKED_UPLOAD_LABEL}
                  </p>
                )}
                {(isBlocked || kept || item.state !== "reading") && (
                  <p
                    className={cn(
                      "text-xs leading-relaxed text-slate-500",
                      isParsing && "text-sm font-medium text-primary",
                    )}
                  >
                    {reviewStatus({ item, credential })}
                  </p>
                )}
                {(!isBlocked || !kept) && (
                  <p className="text-xs font-medium text-primary">
                    {kept
                      ? "Will be attached when you save."
                      : "Will be discarded from review when you save."}
                  </p>
                )}
              </div>
              {isBlocked && kept && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() =>
                    review.setReviewDocumentKept(item.documentId, false)
                  }
                >
                  Remove file
                </Button>
              )}
              {item.scanStatus === "clean" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 shrink-0 rounded-lg px-2 text-primary"
                  aria-label={`${isPreviewOpen ? "Hide preview of" : "Preview"} ${item.title || item.fileName}`}
                  aria-expanded={isPreviewOpen}
                  onClick={() => togglePreview(item.documentId)}
                >
                  <PreviewIcon className="size-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {selectedPreview && (
        <ProviderEvidencePreview
          key={`${selectedPreview.isReviewDocument}/${selectedPreview.document.id}`}
          scope={scope}
          {...selectedPreview}
        />
      )}
      {review.nonParsingBlocker && (
        <p role="status" className="text-sm text-amber-700">
          {review.nonParsingBlocker}
        </p>
      )}
      {!hasKeptEvidence && hasDocuments && (
        <p className="text-xs text-amber-700">
          No supporting file will be kept. The credential details will still be
          saved.
        </p>
      )}
    </section>
  );
}
